import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import useTitanApp from "@/hooks/useTitanApp";
import {
  updateProductBrand as updateProductBrandAction,
  updateProductStatus as updateProductStatusAction,
  addDoseRecord as addDoseRecordAction,
  recordAuditLink as recordAuditLinkAction,
  createVisit as createVisitAction,
  recordDoseAuditDetail as recordDoseAuditDetailAction,
} from "@/features/travelHealthSlice/travelHealthSlice";
import { getTier2Config } from "@/lib/travelHealth/tier2/tier2ConfigRegistry";
import "@/lib/travelHealth/tier2/index";
import type {
  BrandOption,
  ScheduleOption,
  TravelHealthTier2Config,
} from "@/lib/travelHealth/tier2/tier2Config.types";
import { generateId } from "@/lib/pharmacyFirst/utils/idGenerator";
import {
  updateConsultationActions,
  SaveAIChatMessage,
  setAddPatientAIChatMessage,
} from "@/dispatchers/patientDispatchers";

// ─────────────────────────────────────────────────────────────────────────────
// PROPS
// ─────────────────────────────────────────────────────────────────────────────

interface Tier2SavedState {
  stepIndex: number;
  priorDoseHistoryReviewed: boolean;
  selectedBrandId: string | null;
  selectedScheduleId: string | null;
  offLabelConsentGiven: boolean;
  supplyGatesConfirmed: Record<number, boolean>;
  hasChangedSinceAssessment: boolean | undefined;
  exclusionStep: number;
  cautionStep: number;
  cautionAnswers: Record<string, "yes" | "no">;
  cautionAcknowledged: boolean;
  fitAndWell: boolean | undefined;
  batchNumber: string;
  expiryDate: string;
  injectionSite: string;
  sameLimbSpacingConfirmed: boolean;
  volumeConfirmed: boolean;
  administeredAt: string;
  quantitySupplied: string;
  labelConfirmed: boolean;
  suppliedDate: string;
  documentationChecks: Record<string, boolean>;
  gpNotificationNotes: string;
  adviceConfirmed: boolean;
}

interface TravelHealthTier2RunnerProps {
  planId: string;
  productId: string; // TravelHealthPlanProduct.id
  pgdId: string;
  consultationId: string;
  patientAge: number; // in years
  patientSex: string;
  patientWeightKg: string; // empty string if not captured
  hasAllergyHistory: boolean; // true if any specific allergen/category
  // is ticked in tier1Data.allergies (excluding noneKnown)
  isUnder19: boolean; // drives CHIS notification requirement
  sitesUsedToday: { site: string; productName: string }[];
  onSiteRecorded: (site: string, productName: string) => void;
  restoredState?: Tier2SavedState;
  onAutoSaveSnapshot?: (snapshot: Tier2SavedState) => void;
  onComplete: () => void; // called when pathway finishes
  onCancel: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// STEP KEYS
// ─────────────────────────────────────────────────────────────────────────────

type StepKey =
  | "priorDoseHistory"
  | "brandSelection"
  | "offLabelConsent"
  | "supplyGates"
  | "reconfirmChanges"
  | "exclusionScreening"
  | "cautionReview"
  | "doseAndSchedule"
  | "fitAndWell"
  | "administrationRecord"
  | "supplyRecord"
  | "observationPeriod"
  | "documentationChecklist"
  | "patientAdvice"
  | "complete";

const ADMINISTRATION_STEPS: StepKey[] = [
  "priorDoseHistory",
  "brandSelection",
  "reconfirmChanges",
  "exclusionScreening",
  "cautionReview",
  "doseAndSchedule",
  "offLabelConsent",
  "fitAndWell",
  "administrationRecord",
  "observationPeriod",
  "documentationChecklist",
  "patientAdvice",
  "complete",
];

const SUPPLY_STEPS: StepKey[] = [
  "brandSelection",
  "supplyGates",
  "reconfirmChanges",
  "exclusionScreening",
  "cautionReview",
  "doseAndSchedule",
  "offLabelConsent",
  "fitAndWell",
  "supplyRecord",
  "documentationChecklist",
  "patientAdvice",
  "complete",
];

const STEP_LABELS: Record<StepKey, string> = {
  priorDoseHistory: "Prior dose history",
  brandSelection: "Product selection",
  offLabelConsent: "Off-label consent",
  supplyGates: "Pre-supply gates",
  reconfirmChanges: "Reconfirm since assessment",
  exclusionScreening: "Exclusion screening",
  cautionReview: "Caution review",
  doseAndSchedule: "Dose and schedule",
  fitAndWell: "Fit and well check",
  administrationRecord: "Administration record",
  supplyRecord: "Supply record",
  observationPeriod: "Observation period",
  documentationChecklist: "Documentation checklist",
  patientAdvice: "Patient advice",
  complete: "Complete",
};

// ─────────────────────────────────────────────────────────────────────────────
// FILTER HELPERS — presentation filtering only, values sourced from config
// ─────────────────────────────────────────────────────────────────────────────

function parseWeight(weightKgStr: string): number | null {
  if (weightKgStr.trim() === "") return null;
  const parsed = Number(weightKgStr);
  return Number.isNaN(parsed) ? null : parsed;
}

function filterBrandsForPatient(
  brands: BrandOption[],
  ageYears: number,
  weightKgStr: string,
): BrandOption[] {
  const weight = parseWeight(weightKgStr);
  return brands.filter((brand) => {
    if (ageYears < brand.minimumAgeYears) return false;
    if (brand.maximumAgeYears !== undefined && ageYears > brand.maximumAgeYears)
      return false;
    if (
      brand.minimumWeightKg !== undefined &&
      weight !== null &&
      weight < brand.minimumWeightKg
    )
      return false;
    if (
      brand.maximumWeightKg !== undefined &&
      weight !== null &&
      weight > brand.maximumWeightKg
    )
      return false;
    return true;
  });
}

function filterSchedulesForPatient(
  schedules: ScheduleOption[],
  ageYears: number,
  weightKgStr: string,
): ScheduleOption[] {
  const weight = parseWeight(weightKgStr);
  return schedules.filter((schedule) => {
    if (
      schedule.minimumAgeYears !== undefined &&
      ageYears < schedule.minimumAgeYears
    )
      return false;
    if (
      schedule.maximumAgeYears !== undefined &&
      ageYears > schedule.maximumAgeYears
    )
      return false;
    if (
      schedule.minimumWeightKg !== undefined &&
      weight !== null &&
      weight < schedule.minimumWeightKg
    )
      return false;
    return true;
  });
}

function nowIsoLocalDateTime(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
    now.getDate(),
  )}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}

function isPregnancyRelatedCheck(question: string): boolean {
  const q = question.toLowerCase();
  return (
    q.includes("pregnant") || q.includes("pregnancy") || q.includes("breastfeed")
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

export function TravelHealthTier2Runner({
  planId,
  productId,
  pgdId,
  consultationId,
  patientAge,
  patientSex,
  patientWeightKg,
  hasAllergyHistory,
  isUnder19,
  sitesUsedToday,
  onSiteRecorded,
  restoredState,
  onAutoSaveSnapshot,
  onComplete,
  onCancel,
}: TravelHealthTier2RunnerProps) {
  const dispatch = useDispatch();
  const { appState } = useTitanApp();
  const { currentUser } = appState || {};
  const pharmacistId = currentUser?.emailAddress ?? "unknown";
  const pharmacistName =
    currentUser?.name || currentUser?.userName || "Unknown Pharmacist";
  const plan = useSelector((state: any) =>
    state.travelHealth.plans.find((p: any) => p.id === planId),
  );
  const updateProductBrand = (planId: string, productId: string, brand: string) =>
    dispatch(updateProductBrandAction({ planId, productId, brand }));
  const updateProductStatus = (planId: string, productId: string, status: string) =>
    dispatch(updateProductStatusAction({ planId, productId, status }));
  const addDoseRecord = (planId: string, productId: string, doseEntry: any) =>
    dispatch(addDoseRecordAction({ planId, productId, doseEntry }));
  const recordAuditLink = (planId: string, productId: string, auditRecordId: string, visitId: string) =>
    dispatch(recordAuditLinkAction({ planId, productId, auditRecordId, visitId }));
  const createVisit = (visit: any) => {
    const action = dispatch(createVisitAction(visit));
    return (action as any).payload.id;
  };
  const recordDoseAuditDetail = (detail: any) =>
    dispatch(recordDoseAuditDetailAction(detail));

  const config: TravelHealthTier2Config | undefined = useMemo(
    () => getTier2Config(pgdId),
    [pgdId],
  );

  const product = plan?.basket.find((p) => p.id === productId);

  const wasPreScreened = product?.prescreenCompleted === true;

  const rs = restoredState;

  const [hasChangedSinceAssessment, setHasChangedSinceAssessment] = useState<
    boolean | undefined
  >(rs?.hasChangedSinceAssessment);

  const [stepIndex, setStepIndex] = useState(rs?.stepIndex ?? 0);

  const [priorDoseHistoryReviewed, setPriorDoseHistoryReviewed] =
    useState(rs?.priorDoseHistoryReviewed ?? false);

  const [selectedBrand, setSelectedBrand] = useState<BrandOption | null>(
    null,
  );
  const [selectedSchedule, setSelectedSchedule] =
    useState<ScheduleOption | null>(null);

  const [fitAndWell, setFitAndWell] = useState<boolean | undefined>(
    rs?.fitAndWell,
  );

  const [offLabelConsentGiven, setOffLabelConsentGiven] = useState(
    rs?.offLabelConsentGiven ?? false,
  );

  const [supplyGatesConfirmed, setSupplyGatesConfirmed] = useState<
    Record<number, boolean>
  >(rs?.supplyGatesConfirmed ?? {});

  const [exclusionStep, setExclusionStep] = useState(rs?.exclusionStep ?? 0);
  const [exclusionAnswer, setExclusionAnswer] = useState<
    "yes" | "no" | undefined
  >(undefined);
  const [disqualification, setDisqualification] = useState<{
    message: string;
    referralMessage?: string;
  } | null>(null);

  const [cautionStep, setCautionStep] = useState(rs?.cautionStep ?? 0);
  const [cautionAnswer, setCautionAnswer] = useState<
    "yes" | "no" | undefined
  >(undefined);
  const [cautionAnswers, setCautionAnswers] = useState<
    Record<string, "yes" | "no">
  >(rs?.cautionAnswers ?? {});
  const [cautionAcknowledged, setCautionAcknowledged] = useState(
    rs?.cautionAcknowledged ?? false,
  );

  const [batchNumber, setBatchNumber] = useState(rs?.batchNumber ?? "");
  const [expiryDate, setExpiryDate] = useState(rs?.expiryDate ?? "");
  const AVAILABLE_SITES = [
    "Left deltoid",
    "Right deltoid",
    "Left thigh",
    "Right thigh",
  ] as const;
  const [injectionSite, setInjectionSite] = useState(rs?.injectionSite ?? "");
  const [sameLimbSpacingConfirmed, setSameLimbSpacingConfirmed] =
    useState(rs?.sameLimbSpacingConfirmed ?? false);

  const sameLimbClash = sitesUsedToday.find((s) => {
    if (!injectionSite) return false;
    const limbOf = (site: string) =>
      site.includes("deltoid")
        ? site.split(" ")[0] + " arm"
        : site.split(" ")[0] + " leg";
    return limbOf(s.site) === limbOf(injectionSite) && s.site !== injectionSite;
  });
  const [volumeConfirmed, setVolumeConfirmed] = useState(
    rs?.volumeConfirmed ?? false,
  );
  const [administeredAt, setAdministeredAt] = useState(
    rs?.administeredAt ?? nowIsoLocalDateTime(),
  );

  const [quantitySupplied, setQuantitySupplied] = useState(
    rs?.quantitySupplied ?? "",
  );
  const [labelConfirmed, setLabelConfirmed] = useState(
    rs?.labelConfirmed ?? false,
  );
  const [suppliedDate, setSuppliedDate] = useState(
    rs?.suppliedDate ?? todayIsoDate(),
  );

  const [observationStarted, setObservationStarted] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const [observationConfirmed, setObservationConfirmed] = useState(false);
  const [hadAdverseReaction, setHadAdverseReaction] = useState<
    boolean | undefined
  >(undefined);
  const [adverseReactionDetails, setAdverseReactionDetails] = useState("");
  const [yellowCardSubmitted, setYellowCardSubmitted] = useState(false);

  const [documentationChecks, setDocumentationChecks] = useState<
    Record<string, boolean>
  >(rs?.documentationChecks ?? {});
  const [gpNotificationNotes, setGpNotificationNotes] = useState(
    rs?.gpNotificationNotes ?? "",
  );

  const [adviceConfirmed, setAdviceConfirmed] = useState(
    rs?.adviceConfirmed ?? false,
  );

  const [completionData, setCompletionData] = useState<{
    doseNumber: number;
    doseIsLastInCourse: boolean;
    auditRecordId: string;
  } | null>(null);

  const hasRunCompletion = useRef(false);

  // ── Eligible option sets (derived from config + patient) ────────────────

  const eligibleBrands = useMemo(
    () =>
      config ? filterBrandsForPatient(config.brands, patientAge, patientWeightKg) : [],
    [config, patientAge, patientWeightKg],
  );

  const eligibleSchedules = useMemo(
    () =>
      config
        ? filterSchedulesForPatient(config.schedules, patientAge, patientWeightKg)
        : [],
    [config, patientAge, patientWeightKg],
  );

  // Pregnancy status can change between pre-screening and the day of
  // supply, so it must always be reconfirmed even for a pre-screened
  // patient — unless AHT's "not shown to males" rule applies.
  const pregnancyExclusionsToReconfirm = useMemo(() => {
    if (!config || patientSex === "Male") return [];
    return config.exclusions.filter((c) => isPregnancyRelatedCheck(c.question));
  }, [config, patientSex]);

  const pregnancyCautionsToReconfirm = useMemo(() => {
    if (!config || patientSex === "Male") return [];
    return config.cautions.filter((c) => isPregnancyRelatedCheck(c.question));
  }, [config, patientSex]);

  // Restore brand/schedule from saved IDs on mount.
  const hasRestoredSelections = useRef(false);
  useEffect(() => {
    if (hasRestoredSelections.current || !rs) return;
    hasRestoredSelections.current = true;
    if (rs.selectedBrandId) {
      const found = eligibleBrands.find((b) => b.brandId === rs.selectedBrandId);
      if (found) setSelectedBrand(found);
    }
    if (rs.selectedScheduleId) {
      const found = eligibleSchedules.find((s) => s.scheduleId === rs.selectedScheduleId);
      if (found) setSelectedSchedule(found);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligibleBrands, eligibleSchedules]);

  // Auto-select single-eligible brand/schedule so those screens are skipped.
  useEffect(() => {
    if (eligibleBrands.length === 1 && !selectedBrand) {
      setSelectedBrand(eligibleBrands[0]);
    }
  }, [eligibleBrands, selectedBrand]);

  useEffect(() => {
    if (eligibleSchedules.length === 1 && !selectedSchedule) {
      setSelectedSchedule(eligibleSchedules[0]);
    }
  }, [eligibleSchedules, selectedSchedule]);

  const needsOffLabelConsent = selectedSchedule?.offLabel ?? false;

  // ── Auto-save snapshot reporting ──────────────────────────────────────────
  const onAutoSaveSnapshotRef = useRef(onAutoSaveSnapshot);
  onAutoSaveSnapshotRef.current = onAutoSaveSnapshot;

  useEffect(() => {
    if (completionData) return;
    onAutoSaveSnapshotRef.current?.({
      stepIndex,
      priorDoseHistoryReviewed,
      selectedBrandId: selectedBrand?.brandId ?? null,
      selectedScheduleId: selectedSchedule?.scheduleId ?? null,
      offLabelConsentGiven,
      supplyGatesConfirmed,
      hasChangedSinceAssessment,
      exclusionStep,
      cautionStep,
      cautionAnswers,
      cautionAcknowledged,
      fitAndWell,
      batchNumber,
      expiryDate,
      injectionSite,
      sameLimbSpacingConfirmed,
      volumeConfirmed,
      administeredAt,
      quantitySupplied,
      labelConfirmed,
      suppliedDate,
      documentationChecks,
      gpNotificationNotes,
      adviceConfirmed,
    });
  }, [
    stepIndex, priorDoseHistoryReviewed, selectedBrand, selectedSchedule,
    offLabelConsentGiven, supplyGatesConfirmed, hasChangedSinceAssessment,
    exclusionStep, cautionStep, cautionAnswers, cautionAcknowledged, fitAndWell,
    batchNumber, expiryDate, injectionSite, sameLimbSpacingConfirmed,
    volumeConfirmed, administeredAt, quantitySupplied, labelConfirmed,
    suppliedDate, documentationChecks, gpNotificationNotes, adviceConfirmed,
    completionData,
  ]);

  if (!config) {
    return (
      <ErrorScreen
        message="No pathway configuration found for this product. Contact your system administrator."
        onCancel={onCancel}
      />
    );
  }

  if (!plan || !product) {
    return (
      <ErrorScreen
        message="This plan or product could not be found."
        onCancel={onCancel}
      />
    );
  }

  const steps =
    config.serviceType === "administration" ? ADMINISTRATION_STEPS : SUPPLY_STEPS;

  const isStepApplicable = (step: StepKey): boolean => {
    switch (step) {
      case "priorDoseHistory":
        return config.capturesPriorDoseHistory;
      case "brandSelection":
        return eligibleBrands.length !== 1;
      case "offLabelConsent":
        return needsOffLabelConsent;
      case "supplyGates":
        return !wasPreScreened && config.supplyGates.length > 0;
      case "reconfirmChanges":
        return wasPreScreened;
      case "exclusionScreening":
        return (
          !wasPreScreened ||
          hasChangedSinceAssessment === true ||
          pregnancyExclusionsToReconfirm.length > 0
        );
      case "cautionReview":
        return (
          !wasPreScreened ||
          hasChangedSinceAssessment === true ||
          pregnancyCautionsToReconfirm.length > 0
        );
      default:
        return true;
    }
  };

  const currentStep = steps[stepIndex];

  const goToNext = () => {
    let next = stepIndex + 1;
    while (next < steps.length && !isStepApplicable(steps[next])) {
      next += 1;
    }
    setStepIndex(Math.min(next, steps.length - 1));
  };

  const goToBack = () => {
    if (observationStarted && !observationConfirmed) return;
    if (currentStep === "complete") return;
    let prev = stepIndex - 1;
    while (prev >= 0 && !isStepApplicable(steps[prev])) {
      prev -= 1;
    }
    const newIndex = Math.max(prev, 0);
    if (steps[newIndex] === "fitAndWell") {
      setFitAndWell(undefined);
    }
    setStepIndex(newIndex);
  };

  const canGoBack =
    stepIndex > 0 && currentStep !== "complete" && !observationStarted;

  // ── Exclusion screening ──────────────────────────────────────────────────
  // When pre-screened, only pregnancy-related exclusions are re-asked —
  // stable facts already captured stand, but pregnancy status can change.

  const exclusionChecksToShow =
    !wasPreScreened || hasChangedSinceAssessment === true
      ? config.exclusions
      : pregnancyExclusionsToReconfirm;

  const exclusionCheck = exclusionChecksToShow[exclusionStep];

  const confirmExclusionAnswer = () => {
    if (!exclusionCheck || exclusionAnswer === undefined) return;
    if (exclusionAnswer === exclusionCheck.disqualifyingAnswer) {
      setDisqualification({
        message: exclusionCheck.disqualificationMessage,
        referralMessage: exclusionCheck.referralMessage,
      });
      return;
    }
    setExclusionAnswer(undefined);
    if (exclusionStep + 1 < exclusionChecksToShow.length) {
      setExclusionStep(exclusionStep + 1);
    } else {
      goToNext();
    }
  };

  // ── Caution review ──────────────────────────────────────────────────────
  // Same pre-screened narrowing as exclusions above.

  const cautionChecksToShow =
    !wasPreScreened || hasChangedSinceAssessment === true
      ? config.cautions
      : pregnancyCautionsToReconfirm;

  const cautionCheck = cautionChecksToShow[cautionStep];
  const stillAskingCautions = cautionStep < cautionChecksToShow.length;
  const triggeredCautions = cautionChecksToShow.filter(
    (caution) => cautionAnswers[caution.checkId] === "yes",
  );

  const confirmCautionAnswer = () => {
    if (!cautionCheck || cautionAnswer === undefined) return;
    const updated = { ...cautionAnswers, [cautionCheck.checkId]: cautionAnswer };
    setCautionAnswers(updated);
    setCautionAcknowledged(false);
    setCautionAnswer(undefined);
    setCautionStep(cautionStep + 1);
  };

  useEffect(() => {
    if (
      currentStep === "cautionReview" &&
      !stillAskingCautions &&
      triggeredCautions.length === 0
    ) {
      goToNext();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, stillAskingCautions, triggeredCautions.length]);

  // ── Observation timer ────────────────────────────────────────────────────

  const observationMinutes = hasAllergyHistory
    ? config.extendedObservationMinutes
    : config.standardObservationMinutes;

  useEffect(() => {
    if (!observationStarted) return;
    if (secondsRemaining <= 0) return;
    const interval = setInterval(() => {
      setSecondsRemaining((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [observationStarted, secondsRemaining]);

  const startObservation = () => {
    setObservationStarted(true);
    setSecondsRemaining(observationMinutes * 60);
  };

  // ── Documentation checklist item keys ────────────────────────────────────

  const documentationItems: { key: string; label: string }[] = [
    { key: "record-provided", label: "Written vaccination/supply record provided to the patient" },
  ];
  if (config.requiresGPNotification) {
    documentationItems.push({
      key: "gp-notification",
      label: "GP notification sent or declined by patient (document outcome below)",
    });
  }
  if (config.requiresCHISNotificationUnder19 && isUnder19) {
    documentationItems.push({
      key: "chis-notification",
      label: "CHIS notification completed for this patient (under 19)",
    });
  }
  if (config.requiresICVP) {
    documentationItems.push({
      key: "icvp-issued",
      label:
        "ICVP (International Certificate of Vaccination or Prophylaxis) issued to patient using the physical certificate",
    });
  }
  if (config.requiresLariamCard) {
    documentationItems.push({
      key: "lariam-card",
      label: "Lariam Patient Alert Card provided to patient and documented",
    });
  }
  if (config.requiresYellowCardDiscussion) {
    documentationItems.push({
      key: "yellow-card",
      label: "Yellow Card reporting discussed with patient (\u25BC additional monitoring product)",
    });
  }

  const allDocumentationChecked = documentationItems.every(
    (item) => documentationChecks[item.key],
  );

  // ── Completion ───────────────────────────────────────────────────────────

  useEffect(() => {
    if (currentStep !== "complete" || hasRunCompletion.current) return;
    if (!selectedBrand || !selectedSchedule) return;
    hasRunCompletion.current = true;

    const doseIsLastInCourse =
      config.serviceType === "supply"
        ? true
        : selectedSchedule.doses.length === 1
          ? true
          : product.currentDoseNumber + 1 >= selectedSchedule.doses.length;

    const auditRecordId = generateId("TRAVELAUDIT");
    const doseNumber = product.currentDoseNumber + 1;
    const nowIso = new Date().toISOString();

    updateProductBrand(planId, productId, selectedBrand.brandName);
    updateProductStatus(
      planId,
      productId,
      doseIsLastInCourse ? "complete" : "in-progress",
    );
    addDoseRecord(planId, productId, {
      doseNumber,
      scheduledDate: nowIso,
      administeredDate: nowIso,
      auditRecordId,
    });

    const visitId = createVisit({
      planId,
      date: todayIsoDate(),
      pharmacistId,
      pharmacistName,
      productsActioned: [productId],
      auditRecordIds: [auditRecordId],
    });

    recordAuditLink(planId, productId, auditRecordId, visitId);

    if (config.serviceType === "administration") {
      onSiteRecorded(injectionSite, config.displayName);
    }

    recordDoseAuditDetail({
      auditRecordId,
      planId,
      productId,
      doseNumber,
      serviceType: config.serviceType,
      brandName: selectedBrand.brandName,
      route: selectedBrand?.route ?? "",
      batchNumber,
      expiryDate,
      injectionSite:
        config.serviceType === "administration" ? injectionSite : undefined,
      administeredAt:
        config.serviceType === "administration" ? administeredAt : suppliedDate,
      observationMinutes:
        config.serviceType === "administration" ? observationMinutes : undefined,
      adverseReaction:
        hadAdverseReaction === true
          ? {
              occurred: true,
              details: adverseReactionDetails,
              yellowCardSubmitted,
            }
          : { occurred: false },
      gpNotificationNotes,
      recordedAt: nowIso,
    });

    const vaccinationAuditData = {
      vaccinationDetails: {
        vaccinations: [
          {
            vaccineName: config.displayName,
            brandName: selectedBrand.brandName,
            dose: `Dose ${doseNumber}`,
            schedule: selectedSchedule.label,
            route: selectedBrand.route ?? "",
            site: config.serviceType === "administration" ? injectionSite : "",
            manufacturer: selectedBrand.brandName,
            batchNumber,
            expiryDate,
          },
        ],
      },
      pgdId,
      serviceType: config.serviceType,
      auditRecordId,
      adverseReaction:
        hadAdverseReaction === true
          ? {
              occurred: true,
              details: adverseReactionDetails,
              yellowCardSubmitted,
            }
          : { occurred: false },
      gpNotificationNotes,
      pharmacistName,
      pharmacistId,
      timestamp: nowIso,
    };
    const vaccinationPayload = {
      payload: JSON.stringify(vaccinationAuditData),
      actionName: "RecordVaccination",
      actionNumber: "6",
      isPerformed: true,
      performedAt: nowIso,
    };
    updateConsultationActions({
      consultationId,
      payload: vaccinationPayload,
    }).then(() => {
      const chatMessage = {
        sender: "success",
        message: `${config.displayName} — Dose ${doseNumber} ${config.serviceType === "administration" ? "administered" : "supplied"}.`,
        actionId: 6,
        details: vaccinationPayload,
      };
      SaveAIChatMessage({
        ConsultationChat: JSON.stringify([chatMessage]),
        ConsultationId: consultationId,
      });
      setAddPatientAIChatMessage(chatMessage);
    });

    setCompletionData({ doseNumber, doseIsLastInCourse, auditRecordId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  // ── Step content ─────────────────────────────────────────────────────────

  const stepPosition = stepIndex + 1;
  const totalSteps = steps.length;

  return (
    <div className="p-6 space-y-6">
      <div>
        <p className="text-sm text-gray-500">
          Step {stepPosition} of {totalSteps} — {STEP_LABELS[currentStep]}
        </p>
        <h2 className="text-lg font-semibold text-gray-900">
          {config.displayName}
        </h2>
      </div>

      {currentStep === "priorDoseHistory" && (
        <div className="space-y-4">
          <p className="text-sm text-gray-700">{config.indicationSummary}</p>
          {product.priorDoseHistory.length > 0 ? (
            <ul className="space-y-2">
              {product.priorDoseHistory.map((entry, index) => (
                <li key={index} className="border rounded-md px-3 py-2 text-sm">
                  <p className="font-medium">{entry.productName}</p>
                  <p className="text-gray-600">
                    Dose {entry.doseNumber} — {entry.dateGiven}
                    {entry.givenElsewhere ? " (given elsewhere)" : ""}
                  </p>
                  {entry.notes && <p className="text-gray-500">{entry.notes}</p>}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500">No prior dose history recorded.</p>
          )}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="prior-dose-reviewed"
              checked={priorDoseHistoryReviewed}
              onCheckedChange={(checked) =>
                setPriorDoseHistoryReviewed(checked === true)
              }
            />
            <Label htmlFor="prior-dose-reviewed" className="font-normal">
              Prior dose history reviewed
            </Label>
          </div>
        </div>
      )}

      {currentStep === "brandSelection" && (
        <div className="space-y-4">
          {eligibleBrands.length === 0 ? (
            <ErrorPanel message="No eligible product found for this patient's age and weight. Review eligibility or refer." />
          ) : (
            <RadioGroup
              value={selectedBrand?.brandId}
              onValueChange={(value) =>
                setSelectedBrand(
                  eligibleBrands.find((b) => b.brandId === value) ?? null,
                )
              }
              className="flex flex-col gap-2"
            >
              {eligibleBrands.map((brand) => (
                <div
                  key={brand.brandId}
                  className="flex items-start space-x-2 border rounded-md px-3 py-2"
                >
                  <RadioGroupItem
                    value={brand.brandId}
                    id={`brand-${brand.brandId}`}
                    className="mt-1"
                  />
                  <Label
                    htmlFor={`brand-${brand.brandId}`}
                    className="font-normal cursor-pointer"
                  >
                    <span className="block font-medium text-gray-900">
                      {brand.brandName}
                    </span>
                    <span className="block text-sm text-gray-600">
                      {brand.doseDescription}
                    </span>
                    {brand.administrationNotes && (
                      <span className="block text-xs text-gray-500">
                        {brand.administrationNotes}
                      </span>
                    )}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          )}
        </div>
      )}

      {currentStep === "offLabelConsent" && (
        <div className="space-y-4">
          <Alert>
            <AlertTitle>Off-label use — consent required</AlertTitle>
            <AlertDescription>
              {selectedSchedule?.offLabel && selectedSchedule.offLabelRationale
                ? selectedSchedule.offLabelRationale
                : "This product is used outside its marketing authorisation for this indication."}
            </AlertDescription>
          </Alert>
          <div className="flex items-start space-x-2">
            <Checkbox
              id="off-label-consent"
              checked={offLabelConsentGiven}
              onCheckedChange={(checked) =>
                setOffLabelConsentGiven(checked === true)
              }
            />
            <Label htmlFor="off-label-consent" className="font-normal">
              The patient has been informed that this use is outside the
              product licence and has given informed consent. This has been
              documented.
            </Label>
          </div>
        </div>
      )}

      {currentStep === "supplyGates" && (
        <div className="space-y-3">
          {config.supplyGates.map((gate, index) => (
            <div key={index} className="flex items-start space-x-2">
              <Checkbox
                id={`supply-gate-${index}`}
                checked={supplyGatesConfirmed[index] === true}
                onCheckedChange={(checked) =>
                  setSupplyGatesConfirmed((prev) => ({
                    ...prev,
                    [index]: checked === true,
                  }))
                }
              />
              <Label htmlFor={`supply-gate-${index}`} className="font-normal">
                {gate}
              </Label>
            </div>
          ))}
        </div>
      )}

      {currentStep === "reconfirmChanges" && (
        <div className="space-y-4">
          <p className="text-sm font-medium text-gray-900">
            Has anything changed since the patient's pre-screening
            assessment - any new medicines, conditions, or allergies?
          </p>
          <div className="flex gap-2">
            <Button
              type="button"
              variant={hasChangedSinceAssessment === false ? "default" : "outline"}
              onClick={() => setHasChangedSinceAssessment(false)}
            >
              No
            </Button>
            <Button
              type="button"
              variant={hasChangedSinceAssessment === true ? "default" : "outline"}
              onClick={() => setHasChangedSinceAssessment(true)}
            >
              Yes
            </Button>
          </div>
          {hasChangedSinceAssessment === true && (
            <p className="text-sm text-amber-700">
              The full exclusion and caution screening for this product will
              be repeated, since we don't know what has changed.
            </p>
          )}
        </div>
      )}

      {currentStep === "exclusionScreening" && (
        <div className="space-y-4">
          {disqualification ? (
            <div className="space-y-3">
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Cannot proceed</AlertTitle>
                <AlertDescription>{disqualification.message}</AlertDescription>
              </Alert>
              {disqualification.referralMessage && (
                <Alert>
                  <AlertDescription>
                    {disqualification.referralMessage}
                  </AlertDescription>
                </Alert>
              )}
              <Button
                onClick={onCancel}
                className="bg-slate-900 hover:bg-slate-800"
              >
                Close and refer
              </Button>
            </div>
          ) : exclusionCheck ? (
            <div className="space-y-3">
              {wasPreScreened && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2">
                  Pregnancy status must be reconfirmed on the day, even though
                  this consultation was pre-screened — this can change
                  between pre-screening and today.
                </p>
              )}
              <p className="text-sm font-medium text-gray-900">
                {exclusionCheck.question}
              </p>
              <div className="flex gap-2">
                <Button
                  variant={exclusionAnswer === "yes" ? "default" : "outline"}
                  onClick={() => setExclusionAnswer("yes")}
                >
                  Yes
                </Button>
                <Button
                  variant={exclusionAnswer === "no" ? "default" : "outline"}
                  onClick={() => setExclusionAnswer("no")}
                >
                  No
                </Button>
              </div>
              <Button
                onClick={confirmExclusionAnswer}
                disabled={exclusionAnswer === undefined}
                className="bg-slate-900 hover:bg-slate-800"
              >
                Next
              </Button>
            </div>
          ) : (
            <p className="text-sm text-gray-500">No exclusion checks for this product.</p>
          )}
        </div>
      )}

      {currentStep === "cautionReview" && (
        <div className="space-y-4">
          {stillAskingCautions && cautionCheck ? (
            <div className="space-y-3">
              {wasPreScreened ? (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2">
                  Pregnancy status must be reconfirmed on the day, even though
                  this consultation was pre-screened — this can change
                  between pre-screening and today.
                </p>
              ) : null}
              <p className="text-sm font-medium text-gray-900">
                {cautionCheck.question}
              </p>
              <div className="flex gap-2">
                <Button
                  variant={cautionAnswer === "yes" ? "default" : "outline"}
                  onClick={() => setCautionAnswer("yes")}
                >
                  Yes
                </Button>
                <Button
                  variant={cautionAnswer === "no" ? "default" : "outline"}
                  onClick={() => setCautionAnswer("no")}
                >
                  No
                </Button>
              </div>
              <Button
                onClick={confirmCautionAnswer}
                disabled={cautionAnswer === undefined}
                className="bg-slate-900 hover:bg-slate-800"
              >
                Next
              </Button>
            </div>
          ) : triggeredCautions.length > 0 ? (
            <div className="space-y-3">
              {triggeredCautions.map((caution) => (
                <div key={caution.checkId} className="border rounded-md p-3">
                  <p className="text-sm text-gray-700">
                    {caution.cautionMessage}
                  </p>
                </div>
              ))}
              <div className="flex items-start space-x-2">
                <Checkbox
                  id="caution-ack"
                  checked={cautionAcknowledged}
                  onCheckedChange={(checked) =>
                    setCautionAcknowledged(checked === true)
                  }
                />
                <Label htmlFor="caution-ack" className="font-normal">
                  I have reviewed all cautions above and will apply them to
                  my clinical decision.
                </Label>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500">No cautions triggered.</p>
          )}
        </div>
      )}

      {currentStep === "doseAndSchedule" && (
        <div className="space-y-4">
          {selectedBrand && (
            <div className="border rounded-md p-3">
              <p className="text-xs font-medium text-gray-500">Selected product</p>
              <p className="text-sm text-gray-900">{selectedBrand.brandName}</p>
              <p className="text-sm text-gray-600">{selectedBrand.doseDescription}</p>
            </div>
          )}

          {eligibleSchedules.length === 0 ? (
            <ErrorPanel message="No eligible schedule found for this patient's age and weight. Review eligibility or refer." />
          ) : eligibleSchedules.length === 1 ? (
            <ScheduleDetail schedule={eligibleSchedules[0]} />
          ) : (
            <div className="space-y-3">
              <RadioGroup
                value={selectedSchedule?.scheduleId}
                onValueChange={(value) =>
                  setSelectedSchedule(
                    eligibleSchedules.find((s) => s.scheduleId === value) ??
                      null,
                  )
                }
                className="flex flex-col gap-2"
              >
                {eligibleSchedules.map((schedule) => (
                  <div
                    key={schedule.scheduleId}
                    className="flex items-center space-x-2 border rounded-md px-3 py-2"
                  >
                    <RadioGroupItem
                      value={schedule.scheduleId}
                      id={`schedule-${schedule.scheduleId}`}
                    />
                    <Label
                      htmlFor={`schedule-${schedule.scheduleId}`}
                      className="font-normal"
                    >
                      {schedule.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
              {selectedSchedule && <ScheduleDetail schedule={selectedSchedule} />}
            </div>
          )}
        </div>
      )}

      {currentStep === "administrationRecord" && (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Route</Label>
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-gray-900 border rounded px-3 py-2 bg-gray-50">
                {selectedBrand?.route ?? "-"}
              </p>
              <span className="text-xs text-gray-500">
                Enforced for this product, not selectable
              </span>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="batch-number">Batch number</Label>
            <Input
              id="batch-number"
              value={batchNumber}
              onChange={(e) => setBatchNumber(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="expiry-date">Expiry date</Label>
            <Input
              id="expiry-date"
              type="date"
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="injection-site">Injection site</Label>
            <select
              id="injection-site"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
              value={injectionSite}
              onChange={(e) => {
                setInjectionSite(e.target.value);
                setSameLimbSpacingConfirmed(false);
              }}
            >
              <option value="">Select a site</option>
              {AVAILABLE_SITES.map((site) => (
                <option key={site} value={site}>
                  {site}
                </option>
              ))}
            </select>

            {sitesUsedToday.length > 0 && (
              <div className="text-xs text-gray-500 space-y-0.5">
                <p className="font-medium">Already used this visit:</p>
                {sitesUsedToday.map((s, i) => (
                  <p key={i}>
                    {s.site} - {s.productName}
                  </p>
                ))}
              </div>
            )}

            {sameLimbClash && (
              <div className="border border-amber-300 bg-amber-50 rounded p-2 space-y-2">
                <p className="text-sm text-amber-800">
                  {sameLimbClash.site} was already used this visit for{" "}
                  {sameLimbClash.productName}, in the same limb as{" "}
                  {injectionSite}. Injections in the same limb must be at
                  least 2.5 cm apart.
                </p>
                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="same-limb-spacing-confirmed"
                    checked={sameLimbSpacingConfirmed}
                    onCheckedChange={(checked) =>
                      setSameLimbSpacingConfirmed(checked === true)
                    }
                  />
                  <Label
                    htmlFor="same-limb-spacing-confirmed"
                    className="font-normal"
                  >
                    Confirmed at least 2.5 cm from the other injection in this limb
                  </Label>
                </div>
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="administered-at">Date and time of administration</Label>
            <Input
              id="administered-at"
              type="datetime-local"
              value={administeredAt}
              onChange={(e) => setAdministeredAt(e.target.value)}
            />
          </div>
          <div className="flex items-start space-x-2">
            <Checkbox
              id="volume-confirmed"
              checked={volumeConfirmed}
              onCheckedChange={(checked) => setVolumeConfirmed(checked === true)}
            />
            <Label htmlFor="volume-confirmed" className="font-normal">
              Volume/dose administered as per selected product
            </Label>
          </div>
        </div>
      )}

      {currentStep === "supplyRecord" && (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="supply-batch-number">Batch number</Label>
            <Input
              id="supply-batch-number"
              value={batchNumber}
              onChange={(e) => setBatchNumber(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="supply-expiry-date">Expiry date</Label>
            <Input
              id="supply-expiry-date"
              type="date"
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="quantity-supplied">Quantity supplied</Label>
            <Input
              id="quantity-supplied"
              placeholder="e.g. 28 tablets"
              value={quantitySupplied}
              onChange={(e) => setQuantitySupplied(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="supplied-date">Date of supply</Label>
            <Input
              id="supplied-date"
              type="date"
              value={suppliedDate}
              onChange={(e) => setSuppliedDate(e.target.value)}
            />
          </div>
          <div className="flex items-start space-x-2">
            <Checkbox
              id="label-confirmed"
              checked={labelConfirmed}
              onCheckedChange={(checked) => setLabelConfirmed(checked === true)}
            />
            <Label htmlFor="label-confirmed" className="font-normal">
              Product labelled to prescription standard
            </Label>
          </div>
        </div>
      )}

      {currentStep === "fitAndWell" && (
        <div className="space-y-4">

          {fitAndWell === false ? (
            // Deferral panel — not a disqualification
            <div className="space-y-3">
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Vaccination deferred</AlertTitle>
                <AlertDescription>
                  The patient is currently unwell or feverish.
                  Do not proceed today. Document the deferral
                  and rebook the appointment when the patient
                  is well.
                </AlertDescription>
              </Alert>
              <Button
                onClick={onCancel}
                className="bg-slate-900 hover:bg-slate-800"
              >
                Close and rebook
              </Button>
            </div>
          ) : (
            // Question
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-900">
                Is the patient fit and well today — not feeling
                unwell or feverish?
              </p>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant={fitAndWell === true ? "default" : "outline"}
                  className="flex-1"
                  onClick={() => setFitAndWell(true)}
                >
                  Yes — fit and well
                </Button>
                <Button
                  type="button"
                  variant={
                    (fitAndWell as boolean | undefined) === false
                      ? "default"
                      : "outline"
                  }
                  className="flex-1"
                  onClick={() => setFitAndWell(false)}
                >
                  No — unwell or feverish
                </Button>
              </div>
            </div>
          )}

        </div>
      )}

      {currentStep === "observationPeriod" && (
        <div className="space-y-4">
          <p className="text-sm font-medium text-gray-900">
            Observation period: {observationMinutes} minutes
          </p>
          {hasAllergyHistory && (
            <p className="text-sm text-amber-700">
              Extended — allergy history recorded
            </p>
          )}
          {!observationStarted ? (
            <Button
              onClick={startObservation}
              className="bg-slate-900 hover:bg-slate-800"
            >
              Start observation
            </Button>
          ) : (
            <div className="space-y-4">
              <p className="text-2xl font-mono text-gray-900">
                {String(Math.floor(secondsRemaining / 60)).padStart(2, "0")}:
                {String(secondsRemaining % 60).padStart(2, "0")}
              </p>
              {secondsRemaining <= 0 && (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-gray-900">
                    Did the patient have any reaction or adverse event during observation?
                  </p>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={hadAdverseReaction === false ? "default" : "outline"}
                      onClick={() => setHadAdverseReaction(false)}
                    >
                      No
                    </Button>
                    <Button
                      type="button"
                      variant={hadAdverseReaction === true ? "default" : "outline"}
                      onClick={() => setHadAdverseReaction(true)}
                    >
                      Yes
                    </Button>
                  </div>
                  {hadAdverseReaction === true && (
                    <div className="space-y-3 border border-amber-300 bg-amber-50 rounded p-3">
                      <div className="space-y-2">
                        <Label htmlFor="adverse-reaction-details">
                          Describe the reaction and action taken
                        </Label>
                        <Textarea
                          id="adverse-reaction-details"
                          value={adverseReactionDetails}
                          onChange={(e) => setAdverseReactionDetails(e.target.value)}
                          rows={3}
                        />
                      </div>
                      <div className="flex items-start space-x-2">
                        <Checkbox
                          id="yellow-card-submitted"
                          checked={yellowCardSubmitted}
                          onCheckedChange={(checked) =>
                            setYellowCardSubmitted(checked === true)
                          }
                        />
                        <Label htmlFor="yellow-card-submitted" className="font-normal">
                          Yellow Card submitted
                          {config.requiresYellowCardDiscussion &&
                            " (required for this black-triangle product)"}
                        </Label>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {hadAdverseReaction !== undefined && (
              <div className="flex items-start space-x-2">
                <Checkbox
                  id="observation-confirmed"
                  checked={observationConfirmed}
                  onCheckedChange={(checked) =>
                    setObservationConfirmed(checked === true)
                  }
                  disabled={
                    hadAdverseReaction === true &&
                    (!adverseReactionDetails.trim() ||
                      (config.requiresYellowCardDiscussion && !yellowCardSubmitted))
                  }
                />
                <Label htmlFor="observation-confirmed" className="font-normal">
                  {hadAdverseReaction
                    ? "Reaction documented. Fit to leave confirmed."
                    : "Patient observed for " + observationMinutes + " minutes. No adverse reaction. Fit to leave."}
                </Label>
              </div>
              )}
              {secondsRemaining > 0 && (
                <Button
                  variant="outline"
                  onClick={() => setSecondsRemaining(0)}
                >
                  Confirm early completion
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {currentStep === "documentationChecklist" && (
        <div className="space-y-4">
          {documentationItems.map((item) => (
            <div key={item.key} className="flex items-start space-x-2">
              <Checkbox
                id={`doc-${item.key}`}
                checked={documentationChecks[item.key] === true}
                onCheckedChange={(checked) =>
                  setDocumentationChecks((prev) => ({
                    ...prev,
                    [item.key]: checked === true,
                  }))
                }
              />
              <Label htmlFor={`doc-${item.key}`} className="font-normal">
                {item.label}
              </Label>
            </div>
          ))}
          {config.requiresGPNotification && (
            <div className="space-y-2 pl-6">
              <Label htmlFor="gp-notification-notes">GP notification notes</Label>
              <Textarea
                id="gp-notification-notes"
                value={gpNotificationNotes}
                onChange={(e) => setGpNotificationNotes(e.target.value)}
                rows={2}
              />
            </div>
          )}
        </div>
      )}

      {currentStep === "patientAdvice" && (
        <div className="space-y-4">
          <ol className="list-decimal list-inside space-y-2">
            {config.patientAdvicePoints.map((point, index) => (
              <li key={index} className="text-sm text-gray-700">
                {point}
              </li>
            ))}
          </ol>
          <div className="flex items-start space-x-2">
            <Checkbox
              id="advice-confirmed"
              checked={adviceConfirmed}
              onCheckedChange={(checked) => setAdviceConfirmed(checked === true)}
            />
            <Label htmlFor="advice-confirmed" className="font-normal">
              I have provided the patient with the above information.
            </Label>
          </div>
        </div>
      )}

      {currentStep === "complete" && completionData && (
        <div className="space-y-4">
          <h3 className="text-base font-semibold text-gray-900">
            Pathway complete
          </h3>
          <p className="text-sm text-gray-700">
            {config.displayName} — dose {completionData.doseNumber} completed
          </p>
          <p className="text-sm text-gray-700">
            Status:{" "}
            {completionData.doseIsLastInCourse
              ? "Complete"
              : "In progress — further dose required"}
          </p>
          {selectedSchedule?.completionNotes && (
            <Alert>
              <AlertDescription>
                {selectedSchedule.completionNotes}
              </AlertDescription>
            </Alert>
          )}
          {config.requiresICVP && (
            <Alert>
              <AlertTitle>{"ICVP issued \u2713"}</AlertTitle>
              <AlertDescription>
                Ensure patient has received the physical certificate before
                leaving.
              </AlertDescription>
            </Alert>
          )}
          <Button
            onClick={onComplete}
            className="bg-slate-900 hover:bg-slate-800"
          >
            Return to plan
          </Button>
        </div>
      )}

      {currentStep !== "complete" && (
        <div className="flex justify-between pt-4 border-t border-gray-200">
          <div className="flex gap-2">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            {canGoBack && (
              <Button variant="ghost" onClick={goToBack}>
                Back
              </Button>
            )}
          </div>
          <Button
            onClick={goToNext}
            disabled={!isCurrentStepComplete()}
            className="bg-slate-900 hover:bg-slate-800"
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );

  function isCurrentStepComplete(): boolean {
    switch (currentStep) {
      case "priorDoseHistory":
        return priorDoseHistoryReviewed;
      case "brandSelection":
        return selectedBrand !== null && eligibleBrands.length > 0;
      case "offLabelConsent":
        return offLabelConsentGiven;
      case "supplyGates":
        return config
          ? config.supplyGates.every((_, index) => supplyGatesConfirmed[index])
          : false;
      case "reconfirmChanges":
        return hasChangedSinceAssessment !== undefined;
      case "exclusionScreening":
        return false; // advanced via the inline per-question Next button
      case "cautionReview":
        return stillAskingCautions ? false : cautionAcknowledged;
      case "doseAndSchedule":
        return selectedSchedule !== null;
      case "fitAndWell":
        return fitAndWell === true;
      case "administrationRecord":
        return (
          batchNumber.trim() !== "" &&
          expiryDate.trim() !== "" &&
          injectionSite.trim() !== "" &&
          (!sameLimbClash || sameLimbSpacingConfirmed) &&
          volumeConfirmed
        );
      case "supplyRecord":
        return (
          batchNumber.trim() !== "" &&
          expiryDate.trim() !== "" &&
          quantitySupplied.trim() !== "" &&
          labelConfirmed
        );
      case "observationPeriod":
        return observationConfirmed;
      case "documentationChecklist":
        return allDocumentationChecked;
      case "patientAdvice":
        return adviceConfirmed;
      default:
        return true;
    }
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

function ErrorScreen({
  message,
  onCancel,
}: {
  message: string;
  onCancel: () => void;
}) {
  return (
    <div className="p-6 space-y-4">
      <ErrorPanel message={message} />
      <Button variant="outline" onClick={onCancel}>
        Close
      </Button>
    </div>
  );
}

function ErrorPanel({ message }: { message: string }) {
  return (
    <Alert variant="destructive">
      <AlertTriangle className="h-4 w-4" />
      <AlertDescription>{message}</AlertDescription>
    </Alert>
  );
}

function ScheduleDetail({ schedule }: { schedule: ScheduleOption }) {
  return (
    <div className="border rounded-md p-3 space-y-2">
      <p className="text-sm font-medium text-gray-900">{schedule.label}</p>
      <ul className="space-y-1">
        {schedule.doses.map((dose) => (
          <li key={dose.doseNumber} className="text-sm text-gray-700">
            {dose.label}
            {dose.intervalFromDose1Days !== undefined &&
              ` (${dose.intervalFromDose1Days} days from dose 1)`}
          </li>
        ))}
      </ul>
      {schedule.boosterInfo && (
        <div>
          <p className="text-xs font-medium text-gray-500">Booster information</p>
          <p className="text-sm text-gray-700">{schedule.boosterInfo}</p>
        </div>
      )}
      {schedule.completionNotes && (
        <p className="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-md p-2">
          {schedule.completionNotes}
        </p>
      )}
    </div>
  );
}

export type { TravelHealthTier2RunnerProps, Tier2SavedState };
